import { PageHero } from "@/components/common/PageHero";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/common/Reveal";
import { Marquee } from "@/components/common/Marquee";
import { CtaBand } from "@/components/common/CtaBand";
import { BeforeAfterGallery } from "@/components/common/BeforeAfterGallery";
import { MapSection } from "@/components/home/MapSection";
import { Seo } from "@/components/common/Seo";
import { buildPersonSchema, buildBreadcrumbSchema } from "@/lib/schema";
import { WHY_CHOOSE, IMAGES, DOCTOR, CONTACT, SKIN_TREATMENTS, HAIR_TREATMENTS } from "@/data/site";
import { Icon } from "@/components/common/Icon";
import { CheckCircle2, GraduationCap, Sparkles, Award, HeartHandshake, Stethoscope, Scissors, ArrowUpRight } from "lucide-react";

const stats = [
  { icon: Award, value: DOCTOR.yearsExperience, label: "Years Experience" },
  { icon: HeartHandshake, value: DOCTOR.patientsTreated, label: "Patients Treated" },
  { icon: Stethoscope, value: "Certified", label: "Implantologist" },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="Dr. Ankesh Seth — Dentist in Varanasi | Dental Surgeon & Implantologist"
        description="Meet Dr. Ankesh Seth — experienced dental surgeon, implantologist, cosmetic dentist and skin & hair aesthetic expert in Varanasi, with 10+ years of experience and 5,000+ patients treated at his Paharia clinic."
        path="/dr-ankesh-seth"
        jsonLd={[
          buildPersonSchema(),
          buildBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Dr. Ankesh Seth", path: "/dr-ankesh-seth" },
          ]),
        ]}
      />
      <PageHero
        eyebrow="About"
        titleLines={["Meet Dr. Ankesh", "Seth"]}
        subtitle={DOCTOR.role + " in Varanasi — dedicated to gentle, patient-first care backed by advanced technology and international training."}
        crumbs={[{ label: "About" }]}
      />

      <section className="bg-white">
        <div className="mx-auto grid max-w-[1440px] items-start gap-12 px-6 py-24 md:px-12 md:py-32 lg:grid-cols-2 lg:gap-20 lg:px-20">
          <Reveal className="lg:sticky lg:top-28">
            <div className="aspect-[4/5] overflow-hidden rounded-[2rem]">
              <img src={IMAGES.doctor} alt="Dr. Ankesh Kr. Seth" className="h-full w-full object-cover" />
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3">
              {stats.map((s) => (
                <div key={s.label} className="rounded-2xl border border-brand-line bg-brand-soft p-4 text-center">
                  <s.icon className="mx-auto mb-1 h-5 w-5 text-brand-blue" />
                  <div className="font-display text-xl font-bold text-brand-dark">{s.value}</div>
                  <div className="text-[11px] leading-tight text-brand-muted">{s.label}</div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <span className="text-sm font-semibold uppercase tracking-[0.2em] text-brand-blue">
              {CONTACT.clinic} · Paharia
            </span>
            <h2 className="mt-4 font-display text-3xl font-semibold leading-tight text-brand-dark md:text-5xl">
              A dentist who puts <span className="italic text-brand-blue">people first</span>
            </h2>
            <div className="mt-6 space-y-4 text-brand-muted">
              {DOCTOR.bio.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            {/* Qualifications */}
            <div className="mt-10 rounded-2xl border border-brand-line p-7">
              <h3 className="flex items-center gap-2 font-display text-xl font-medium text-brand-dark">
                <GraduationCap className="h-5 w-5 text-brand-blue" />
                Professional Qualifications
              </h3>
              <ul className="mt-4 space-y-2.5">
                {DOCTOR.qualifications.map((q) => (
                  <li key={q} className="flex items-start gap-3 text-sm text-brand-dark">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-green" />
                    <span>{q}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Expertise */}
            <div className="mt-6">
              <h3 className="flex items-center gap-2 font-display text-xl font-medium text-brand-dark">
                <Sparkles className="h-5 w-5 text-brand-blue" />
                Areas of Expertise
              </h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {DOCTOR.expertise.map((e) => (
                  <span
                    key={e}
                    className="rounded-full border border-brand-line bg-brand-soft px-4 py-2 text-sm font-medium text-brand-dark"
                  >
                    {e}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Marquee text="Advanced technology — Painless care — Trusted in Varanasi" dark />

      <section className="bg-brand-soft">
        <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-12 md:py-32 lg:px-20">
          <Reveal className="mb-14 max-w-2xl">
            <span className="text-sm font-semibold uppercase tracking-[0.2em] text-brand-blue">
              Our Clinic
            </span>
            <h2 className="mt-5 font-display text-4xl font-semibold leading-tight tracking-tight text-brand-dark md:text-5xl">
              What makes us <span className="italic text-brand-blue">different</span>
            </h2>
          </Reveal>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {WHY_CHOOSE.map((w, i) => (
              <Reveal key={w.title} delay={(i % 3) * 0.08}>
                <div className="h-full rounded-2xl border border-brand-line bg-white p-8">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-blue/10 text-brand-blue">
                    <Icon name={w.icon} className="h-6 w-6" />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-medium text-brand-dark">{w.title}</h3>
                  <p className="mt-2 text-sm text-brand-muted">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="about-aesthetic" className="bg-white">
        <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-12 md:py-32 lg:px-20">
          <Reveal className="mb-12 max-w-3xl">
            <span className="text-sm font-semibold uppercase tracking-[0.2em] text-brand-blue">
              Skin &amp; Hair Expertise
            </span>
            <h2 className="mt-5 font-display text-4xl font-semibold leading-tight tracking-tight text-brand-dark md:text-5xl">
              More than a dentist — a <span className="italic text-brand-blue">skin &amp; hair</span> expert
            </h2>
            <p className="mt-6 text-brand-muted">
              Alongside his dental surgery, Dr. Ankesh Seth holds an MSc in Facial Aesthetics &amp;
              Anti-Aging and a PGDCC in Clinical Cosmetology (Germany). At {CONTACT.clinic} in Paharia,
              Varanasi, this lets him offer safe, medically-guided aesthetic care — helping patients
              look and feel their best with treatments for both skin and hair, all under one trusted roof.
            </p>
          </Reveal>

          <div className="grid gap-6 lg:grid-cols-2">
            <Reveal>
              <div className="h-full rounded-[2rem] border border-brand-line bg-brand-soft p-8">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-blue/10 text-brand-blue">
                  <Sparkles className="h-6 w-6" />
                </span>
                <h3 className="mt-5 font-display text-2xl font-medium text-brand-dark">Skin Treatments</h3>
                <p className="mt-2 text-sm text-brand-muted">
                  Medical-grade skincare for a clear, radiant, youthful complexion.
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {SKIN_TREATMENTS.map((t) => (
                    <Link
                      key={t.slug}
                      to={`/${t.slug}`}
                      className="rounded-full border border-brand-line bg-white px-3.5 py-1.5 text-xs font-medium text-brand-dark transition-colors hover:border-brand-blue/40 hover:text-brand-blue"
                    >
                      {t.name}
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="h-full rounded-[2rem] border border-brand-line bg-brand-soft p-8">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-green/15 text-brand-green">
                  <Scissors className="h-6 w-6" />
                </span>
                <h3 className="mt-5 font-display text-2xl font-medium text-brand-dark">Hair Treatments</h3>
                <p className="mt-2 text-sm text-brand-muted">
                  Restore, strengthen and regrow your hair with advanced therapies.
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {HAIR_TREATMENTS.map((t) => (
                    <Link
                      key={t.slug}
                      to={`/${t.slug}`}
                      className="rounded-full border border-brand-line bg-white px-3.5 py-1.5 text-xs font-medium text-brand-dark transition-colors hover:border-brand-blue/40 hover:text-brand-blue"
                    >
                      {t.name}
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="mt-10">
            <Link
              to="/aesthetic-treatments"
              data-testid="about-aesthetic-viewall"
              className="inline-flex items-center gap-2 rounded-full bg-brand-blue px-7 py-3.5 text-sm font-semibold text-white transition-all hover:bg-brand-bluehover active:scale-95"
            >
              Explore all skin &amp; hair treatments
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <BeforeAfterGallery />
      <CtaBand />
      <MapSection />
    </div>
  );
}